import { useMemo, useState } from 'react';
import { useT } from '@hana/i18n';
import './i18n';
import { IconArrowLeft, IconChecks, IconCalendar, IconDownload, IconLayoutList, IconPhoto, IconPhotoStar, IconPencil, IconThumbUp, IconTrash, IconTrophy, IconUpload } from '@tabler/icons-react';
import { Button, Modal, Segmented, StateView, type ScreenState } from '@hana/ui';
import { filterAlbums, type AlbumRange } from './logic';
import type { FileAlbum } from './types';
import s from './files.module.css';
import l from './library.module.css';

// Mint-Files › tab=media: Bảng tin · Lưới album · mở một album (12 ảnh có điểm AI). Chỉ đọc: nút ghi báo toast.

const RANGES: readonly { key: AlbumRange; label: string }[] = [{ key: 'month', label: 'fl.md.thisMonth' }, { key: '3m', label: 'fl.md.3m' }, { key: 'year', label: 'fl.md.year' }, { key: 'all', label: 'fl.md.all' }];
const SC = [9.1, 8.8, 8.6, 8.4, 8.1, 7.9, 7.6, 7.4, 7.1, 6.8, 6.5, 6.2];
const BY = ['ANHLT', 'ANHLT', 'KietLT', 'DAVID', 'ANHLT', 'HOATTN', 'KietLT', 'ANHLT', 'DAVID', 'AnNV', 'ANHLT', 'KietLT'];
const ACTS: readonly [string, string][] = [
  ['ThuyNTT thích album Hội chợ Vietnam Logistics 2026', '22/Oct 08:51'], ['HOATTN bình luận ở Đóng hàng reefer SAVITA', '18/Oct 11:40'],
  ['HANHLTM @nhắc bạn trong Sinh nhật HANA 10 năm', '06/Sep 09:12'], ['ANHLT thêm 8 ảnh vào Đóng hàng reefer SAVITA', '18/Oct 10:12'],
];
const COMMENTS: Record<number, [string, string, string][]> = {
  1: [['HOATTN', 'Ảnh PTI rõ, gửi hãng Wan Hai được rồi.', '18/Oct 11:40'], ['DAVID', '@ANHLT nhớ chụp thêm biên bản niêm chì nhé.', '18/Oct 12:05']],
  2: [['TUANNT', 'Ảnh gian hàng đẹp, dùng cho bài Facebook công ty.', '13/Oct 08:30']],
};
const LB: readonly [string, string, number, number, number][] = [['ANHLT', 'Field Ops', 132, 8.6, 1240], ['HANHLTM', 'HC–NS', 96, 8.1, 902], ['DAVID', 'Sales', 71, 8.3, 688], ['ThuyNTT', 'CS', 64, 7.9, 571], ['KietLT', 'Field Ops', 52, 7.7, 446]];
const grad = (a: FileAlbum, i: number) => `linear-gradient(135deg, ${a.c[i % 4]}, ${a.c[(i + 1) % 4]})`;
const num = (v: number) => String(v).replace('.', ',');

export function MediaCols({ albums, state, q, album, setAlbum, noWrite, retry }: {
  albums: readonly FileAlbum[]; state: ScreenState; q: string; album: number | null; setAlbum: (id: number | null) => void; noWrite: () => void; retry: () => void;
}) {
  const t = useT();
  const [view, setView] = useState<'feed' | 'albums'>('albums');
  const [range, setRange] = useState<AlbumRange>('all');
  const [lb, setLb] = useState(false);
  const [psel, setPsel] = useState<Record<number, boolean>>({});
  const ok = state === 'ready' || state === 'empty';
  const rows = useMemo(() => (state === 'empty' ? [] : filterAlbums(albums, range, q)), [albums, state, range, q]);
  const cur = album === null ? null : albums.find((a) => a.id === album) ?? null;
  const missing = cur ? [!cur.d && t('fl.md.evDate'), !cur.loc && t('fl.md.place')].filter(Boolean).join(t('fl.md.and')) : '';
  const pn = Object.values(psel).filter(Boolean).length;
  const photos = cur && cur.ph ? SC.map((sc, i) => ({ i, sc, by: BY[i]! })) : [];
  const open = (id: number) => { setAlbum(id); setPsel({}); };
  const counts = (a: FileAlbum) => t('fl.md.photosVideos', { p: a.ph, v: a.vd });
  const rangeN = (k: AlbumRange) => filterAlbums(albums, k).length;

  return (
    <div className={s.cols}>
      <section className={s.c1} aria-label={t('fl.md.nav')}>
        <button type="button" className={s.nav} aria-pressed={view === 'feed' && !cur} onClick={() => { setView('feed'); setAlbum(null); }}><IconLayoutList size={16} aria-hidden="true" /><span className={s.navK}>{t('fl.md.feed')}</span></button>
        <button type="button" className={s.nav} aria-pressed={view === 'albums' && !cur} onClick={() => { setView('albums'); setAlbum(null); }}><IconPhoto size={16} aria-hidden="true" /><span className={s.navK}>{t('fl.md.album')}</span>{ok && <span className={s.navN}>{albums.length}</span>}</button>
        <div className={s.head}>{t('fl.md.dateFilter')}</div>
        {RANGES.map((r) => (
          <button key={r.key} type="button" className={s.nav} aria-pressed={range === r.key} onClick={() => { setRange(r.key); setAlbum(null); }}><IconCalendar size={16} aria-hidden="true" /><span className={s.navK}>{t(r.label)}</span>{ok && <span className={s.navN}>{rangeN(r.key)}</span>}</button>
        ))}
        <div className={s.grow} />
        <button type="button" className={l.lbBtn} onClick={() => setLb(true)}><IconTrophy size={16} aria-hidden="true" /><span>{t('fl.md.lbBtn')}</span></button>
      </section>
      <Modal
        open={lb}
        onOpenChange={setLb}
        title={t('fl.md.lbTitle')}
        description={t('fl.md.lbDesc')}
        footer={<><Button variant="ghost" onClick={() => setLb(false)}>{t('fl.md.close')}</Button><Button variant="primary" onClick={noWrite}>{t('fl.md.export')}</Button></>}
      >
        <table className={l.lbTable}>
          <thead><tr><th>#</th><th>{t('fl.md.thContrib')}</th><th>{t('fl.md.thPhoto')}</th><th>{t('fl.md.thAi')}</th><th>{t('fl.md.thScore')}</th></tr></thead>
          <tbody>{LB.map(([k, dept, ph, ai, pt], i) => <tr key={k}><td>{i + 1}</td><td><b>{k}</b> <span className={l.actW}>{dept}</span></td><td>{ph}</td><td>{num(ai)}</td><td>{pt.toLocaleString('vi-VN')}</td></tr>)}</tbody>
        </table>
      </Modal>

      <section className={s.c2} aria-label={t('fl.md.album')}>
        <div className={s.bar56}>
          {cur && <Button variant="soft" aria-label={t('fl.md.back')} title={t('fl.md.back')} icon={<IconArrowLeft size={16} />} onClick={() => setAlbum(null)} />}
          <b className={l.title}>{cur ? cur.n : view === 'feed' ? t('fl.md.feed') : t('fl.md.album')}</b>
          <span className={l.sub}>{cur ? `${cur.d ? `${cur.d} · ${cur.loc}` : t('fl.md.missingDL')} · ${t('fl.md.photosShown', { n: photos.length })}` : t('fl.md.albumsN', { n: rows.length })}</span>
          <span className={s.grow} />
          {cur ? (pn > 0 && (
            <>
              <b className={l.title} style={{ color: 'var(--link)' }}>{t('fl.md.pickN', { n: pn })}</b>
              {pn === 1 && <Button variant="secondary" icon={<IconPhotoStar size={16} />} onClick={noWrite}>{t('fl.md.cover')}</Button>}
              <Button variant="secondary" icon={<IconDownload size={16} />} onClick={noWrite}>{t('fl.md.origDl')}</Button>
              <Button variant="secondary" icon={<IconTrash size={16} />} onClick={noWrite}>{t('fl.md.delPhoto')}</Button>
            </>
          )) : (
            <Segmented label={t('fl.md.how')} kind="toggle" value={view} onChange={setView} items={[{ key: 'feed', label: t('fl.md.feed') }, { key: 'albums', label: t('fl.md.grid') }]} />
          )}
        </div>
        <div className={`${s.scroll} ${l.mPad}`}>
          <StateView
            state={state === 'noperm' ? 'ready' : state}
            title={state === 'error' ? t('fl.md.errTitle') : t('fl.md.emptyTitle')}
            desc={state === 'error' ? t('fl.md.errDesc') : t('fl.md.emptyDesc')}
            action={state === 'error' ? <Button onClick={retry}>{t('fl.lib.retry')}</Button> : undefined}
            skeleton={<div className={l.mGrid}>{Array.from({ length: 6 }, (_, i) => <div key={i} style={{ height: 210, borderRadius: 16, background: 'var(--sub)' }} />)}</div>}
          >
            {!cur && view === 'feed' && (
              <div className={l.feed}>
                {rows.map((a) => (
                  <article key={a.id} className={l.post}>
                    <div className={l.postH}>
                      <span aria-hidden="true" className={l.ab}>{a.by.slice(0, 2).toUpperCase()}</span>
                      <div className={`${s.grow} ${l.postT}`}><div><b>{a.by}</b> <span>{t('fl.md.added', { n: a.add })}</span> <b>{a.n}</b></div><div className={l.postW}>{a.d ? `${a.d} · ${a.loc}` : t('fl.md.noDateLoc')}</div></div>
                    </div>
                    <button type="button" className={l.mosaic4} aria-label={t('fl.md.openAlbum', { n: a.n })} onClick={() => open(a.id)}>{a.c.map((c, i) => <span key={i} aria-hidden="true" style={{ background: c }} />)}</button>
                    <div className={l.postF}>
                      <Button variant="secondary" icon={<IconThumbUp size={16} />} onClick={noWrite}>{t('fl.md.like', { n: a.likes })}</Button>
                      <Button variant="secondary" onClick={() => open(a.id)}>{t('fl.md.commentsN', { n: (COMMENTS[a.id] ?? []).length })}</Button>
                      <span className={l.postN}>{counts(a)}</span>
                    </div>
                  </article>
                ))}
              </div>
            )}
            {!cur && view === 'albums' && (
              <div className={l.mGrid}>
                {rows.map((a) => (
                  <button key={a.id} type="button" className={l.mCard} onClick={() => open(a.id)}>
                    <span aria-hidden="true" className={l.mosaic2}>{a.c.map((c, i) => <span key={i} style={{ background: c }} />)}</span>
                    <span className={l.mBody}>
                      <b>{a.n}</b>
                      <span className={a.d ? l.mMeta : l.mWarn}>{a.d ? `${a.d} · ${a.loc}` : t('fl.md.noDateLoc')}</span>
                      <span className={l.mMeta}>{counts(a)} · {t('fl.md.likesN', { n: a.likes })}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            {cur && (
              <>
                {missing && <div role="alert" className={l.ro} style={{ marginInline: 0, marginBottom: 12, background: 'var(--warn-bg)' }}><b style={{ color: 'var(--warn)' }}>{t('fl.md.missingA', { v: missing })}</b>{t('fl.md.missingB')}</div>}
                <div className={l.photos}>
                  {photos.map((p) => (
                    <div key={p.i} className={l.photo} style={{ background: grad(cur, p.i), boxShadow: psel[p.i] ? 'inset 0 0 0 3px var(--pri)' : undefined }}>
                      <label style={{ position: 'absolute', top: 8, left: 8 }}><input type="checkbox" aria-label={t('fl.md.pickPhoto', { n: `IMG_${2100 + p.i}.jpg` })} checked={!!psel[p.i]} onChange={() => setPsel({ ...psel, [p.i]: !psel[p.i] })} /></label>
                      <span className={l.aiSc}>AI {num(p.sc)}</span>
                      {p.i === 0 && <span className={l.cover}>{t('fl.md.cvr')}</span>}
                      <span className={l.by}>{p.by}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </StateView>
        </div>
      </section>
    </div>
  );
}

/** Cột phải tab Media: chi tiết album đang mở, hoặc "Hoạt động". */
export function MediaPanel({ album, noWrite }: { album: FileAlbum | null; noWrite: () => void }) {
  const t = useT();
  const [read, setRead] = useState(false);
  if (!album) {
    return (
      <div className={l.panel}>
        <div className={s.dockHint}>{t('fl.md.pickAlbum')}</div>
        <section className={s.dockCard} style={{ padding: 14, borderRadius: 14, background: 'var(--sub)', boxShadow: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h2 style={{ margin: 0, fontSize: 15 }}>{t('fl.md.activity')}</h2>
            <Button variant="ghost" size="sm" style={{ width: 'auto', height: 28, padding: '0 8px', fontSize: 12 }} icon={<IconChecks size={14} />} onClick={() => setRead(true)}>{t('fl.md.markRead')}</Button>
          </div>
          {ACTS.map(([k, w], i) => (
            <div key={k} className={l.act}><span className={l.dot} style={read || i > 2 ? { background: 'transparent' } : undefined} /><div><div>{k}</div><div className={l.actW}>{w}</div></div></div>
          ))}
        </section>
      </div>
    );
  }
  const info: [string, string][] = [[t('fl.md.kDate'), album.d || t('fl.md.none')], [t('fl.md.kLoc'), album.loc || t('fl.md.none')], [t('fl.md.kDesc'), album.desc || '—'], [t('fl.md.kCount'), t('fl.md.photosVideos', { p: album.ph, v: album.vd })], [t('fl.md.kBy'), album.by]];
  return (
    <div className={l.panel}>
      <h2 className={l.pHead} style={{ margin: 0, fontSize: 16 }}>{album.n}</h2>
      <dl className={l.dl}>{info.map(([k, v]) => <div key={k} className={l.dlRow}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
      <div className={l.pBtns}>
        <Button variant="primary" icon={<IconUpload size={16} />} onClick={noWrite}>{t('fl.md.uploadP')}</Button>
        <Button variant="secondary" icon={<IconPencil size={16} />} onClick={noWrite}>{t('fl.md.edit')}</Button>
        <Button variant="secondary" icon={<IconTrash size={16} />} onClick={noWrite}>{t('fl.md.del')}</Button>
      </div>
      <div><Button variant="secondary" icon={<IconThumbUp size={16} />} onClick={noWrite}>{t('fl.md.like', { n: album.likes })}</Button></div>
      <section className={l.ai} style={{ background: 'var(--sub)', boxShadow: 'none' }}>
        <div className={l.aiH} style={{ color: 'var(--tx)' }}>{t('fl.md.comments')}</div>
        {(COMMENTS[album.id] ?? []).map(([who, txt, when]) => <div key={when} className={l.act}><div><div className={l.actW}><b>{who}</b> · {when}</div><div>{txt}</div></div></div>)}
      </section>
    </div>
  );
}
